import { useState, useEffect, useCallback, useMemo } from 'react'

const LOCAL_STORAGE_KEY = 'notifications_store_v2'
const MAX_NOTIFICATIONS = 20

export interface Notification {
  id?: number
  title: string
  message: string
  created_at: number | string
  is_read: boolean
  news_link?: string
  news_title?: string
  origin?: string
  invoice_url?: string
  student_name?: string
  userIds?: string[]
  type?: string
  lead_id?: number | string | null
  provider?: string | null
  integration_id?: number | string | null
  external_channel_id?: string | null
  external_chat_type?: string | null
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>(() => {
    try {
      // The legacy cache may contain events delivered before tenant isolation.
      // Rehydrate trusted history from the platform API instead of merging it back.
      localStorage.removeItem('notifications_store')
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
      return stored ? JSON.parse(stored).slice(0, MAX_NOTIFICATIONS) : []
    } catch (error) {
      console.error('Failed to parse notifications:', error)
      return []
    }
  })

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.is_read).length,
    [notifications],
  )

  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(notifications))
  }, [notifications])

  const addNotification = useCallback((notification: Notification) => {
    setNotifications((prev) => {
      const exists = prev.some(
        (n) =>
          (notification.id && n.id === notification.id) ||
          (n.created_at === notification.created_at &&
            n.title === notification.title),
      )

      if (exists) return prev

      return [notification, ...prev].slice(0, MAX_NOTIFICATIONS)
    })
  }, [])

  const setNotificationsFromServer = useCallback(
    (serverNotifications: Notification[]) => {
      setNotifications((prev) => {
        const merged = [...serverNotifications]

        prev.forEach((notification) => {
          const exists = merged.some(
            (item) =>
              (notification.id && item.id === notification.id) ||
              (item.created_at === notification.created_at &&
                item.title === notification.title),
          )

          if (!exists) {
            merged.push(notification)
          }
        })

        return merged.slice(0, MAX_NOTIFICATIONS)
      })
    },
    [],
  )

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, is_read: true })),
    )
  }, [])

  const markAsRead = useCallback((index: number) => {
    setNotifications((prev) =>
      prev.map((notification, i) =>
        i === index ? { ...notification, is_read: true } : notification,
      ),
    )
  }, [])

  return {
    notifications,
    unreadCount,
    addNotification,
    setNotificationsFromServer,
    markAllAsRead,
    markAsRead,
  }
}
